import * as React from 'react';
import { useState } from 'react';
import { useTheme } from 'styled-components/macro';
import { Dropdown, NewModal, Spinner } from 'lib/components';
import { Tag, Typography } from 'react-covideo-common';
import { TAG_LIMIT } from 'lib/const/VideoTags';
import { Button } from 'react-covideo-common';
import { TagType } from 'react-covideo-common/dist/components/Tag/types';
import { useTranslation } from 'react-i18next';
import { Flex } from '../styles/layout';
import { useGetAllTags } from 'lib/api/videoTags/useGetAllTags';
import { useCreateTagMutation } from 'lib/api/videoTags/useCreateTagMutation';
import { Option } from '../dropdown/Dropdown';

type ModalVideoTagsProps = {
  videoTags: TagType[];
  handleModalClose: () => void;
  onSubmit: (tags: TagType[]) => void;
  title: string;
  buttonTitle: string;
  isSaving?: boolean;
};

export const ModalVideoTags = ({
  videoTags = [], // current video tags
  handleModalClose,
  onSubmit, // this handles sending tags to api
  title,
  buttonTitle,
  isSaving = false,
}: ModalVideoTagsProps) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const themes = useTheme();
  const [selectedTags, setSelectedTags] = useState(videoTags);

  const { mutateAsync: createTag, isLoading: isCreatingTag } =
    useCreateTagMutation();

  const { data: tagsData, isLoading } = useGetAllTags({
    start: 0,
    limit: 50, // limit is ignored on BE if all: true
    search: '',
    all: true,
  });

  const availableTags = React.useMemo(() => {
    if (!tagsData) return [];

    return tagsData
      .filter(tag => !selectedTags.some(t => t.value === tag.tagId))
      .map(tag => ({
        label: tag.tag,
        value: tag.tagId,
      }));
  }, [tagsData, selectedTags]);

  const removeTag = (tag: TagType) => {
    setSelectedTags(prev =>
      prev.filter(selectedTag => selectedTag.value !== tag.value)
    );
  };

  const handleSelectTag = async (tag: TagType) => {
    if (isCreatingTag) {
      return;
    }

    // TO-DO: __isNew__ should be added to TagType on react-common
    // @ts-ignore
    if (tag.__isNew__) {
      const newTag = await createTag({
        tag: tag.label,
      });

      setSelectedTags(prev => [
        ...prev,
        {
          label: newTag.tag,
          value: newTag.tagId,
        },
      ]);
      return;
    }

    const existingTag = selectedTags.find(
      selectedTag => selectedTag.value === tag.value
    );
    if (!!existingTag) {
      // remove
      removeTag(existingTag);
    } else {
      setSelectedTags(prev => [...prev, tag]);
    }
  };

  const onChange = async (tag: Option) => {
    if (!tag.value) return;
    if (tag?.label && tag.label.toString().length > TAG_LIMIT) {
      setErrorMessage(() =>
        t('Tag can not contain more than {{limit}} characters.', {
          limit: TAG_LIMIT,
        })
      );
      return;
    }
    setErrorMessage('');
    await handleSelectTag({
      ...tag,
      label: tag.label.toString(),
    });
  };

  const isSearchDisabled = isCreatingTag || isLoading;
  const isDisabled = !!errorMessage || isSearchDisabled;

  return (
    <NewModal
      closeModal={handleModalClose}
      headerText={title}
      style={{
        content: {
          minHeight: '200px',
        },
      }}
    >
      <Flex>
        <Flex gap='5px'>
          <Dropdown
            value={null}
            options={availableTags}
            onChange={onChange}
            isSearchable
            creatable
            disabled={isSearchDisabled}
            placeholder={isCreatingTag ? 'Creating...' : 'Add tag(s)...'}
            extendStyles={{
              menu: {
                maxHeight: '100px',
              },
              menuList: {
                maxHeight: '100px',
              },
            }}
          />
          {errorMessage != null && (
            <Typography variant='small' color={themes.colors.danger[100]}>
              {errorMessage}
            </Typography>
          )}
        </Flex>

        <Flex
          flexDirection='row'
          gap='5px'
          flexWrap='wrap'
          margin='10px 0'
          width='592px'
        >
          {selectedTags.map((selectedTag, index) => (
            <Tag
              key={`${selectedTag.value}-${index}`}
              tag={selectedTag}
              onRemove={removeTag}
            />
          ))}
        </Flex>
      </Flex>
      <Flex flexDirection='row' justifyContent='flex-end' margin='auto 0 0 0'>
        <Button
          text={buttonTitle}
          onClick={() => onSubmit(selectedTags)}
          disabled={isDisabled}
          loading={isSaving}
          icon={isSaving ? <Spinner /> : undefined}
        />
      </Flex>
    </NewModal>
  );
};
